// Job Application Form Validation Utilities

// Validation patterns
export const VALIDATION_PATTERNS = {
  // Name validation - letters and spaces
  name: /^[a-zA-Z\s]{2,50}$/,

  // Email validation - comprehensive email regex
  email: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,

  // Phone validation - Indian phone numbers (10 digits starting with 1-9)
  phone: /^[1-9]\d{9}$/,

  // Location validation - letters, numbers, spaces, commas, periods, apostrophes, hyphens
  location: /^[a-zA-Z0-9\s,.\-']{2,100}$/,

  // Company name validation - letters, numbers, spaces, common symbols
  company: /^[a-zA-Z0-9\s&.,\-'()]{2,100}$/,

  // Position validation - letters, numbers, spaces, common symbols
  position: /^[a-zA-Z0-9\s&.,\-'()]{2,100}$/,

  // Education validation - letters, numbers, spaces, common symbols
  education: /^[a-zA-Z0-9\s&.,\-'()]{2,100}$/,

  // Skills validation - only alphanumeric characters, comma, and spaces
  skills: /^[a-zA-Z0-9,\s]{2,500}$/,

  // University validation - only alphanumeric characters, comma, and spaces
  university: /^[a-zA-Z0-9,\s]{2,100}$/,

  // Cover letter validation - any characters except excessive whitespace
  coverLetter: /^[\s\S]{0,2000}$/
};

// Validation rules
export const VALIDATION_RULES = {
  firstName: {
    required: true,
    pattern: VALIDATION_PATTERNS.name,
    minLength: 2,
    maxLength: 50,
    message: 'First name must be 2-50 characters, letters and spaces only'
  },
  lastName: {
    required: true,
    pattern: VALIDATION_PATTERNS.name,
    minLength: 2,
    maxLength: 50,
    message: 'Last name must be 2-50 characters, letters and spaces only'
  },
  email: {
    required: true,
    pattern: VALIDATION_PATTERNS.email,
    maxLength: 100,
    message: 'Please enter a valid email address'
  },
  phone: {
    required: true,
    pattern: VALIDATION_PATTERNS.phone,
    minLength: 10,
    maxLength: 10,
    message: 'Phone number must be exactly 10 digits.'
  },
  dateOfBirth: {
    required: true,
    validate: (value) => {
      if (!value) return false; // Required field
      const today = new Date();
      const birthDate = new Date(value);
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();

      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }

      return age >= 18;
    },
    message: 'You must be at least 18 years old'
  },
  gender: {
    required: true,
    validate: (value) => value && ['Male', 'Female', 'Other'].includes(value),
    message: 'Please select a gender'
  },
  currentCompany: {
    required: false,
    pattern: VALIDATION_PATTERNS.company,
    maxLength: 100,
    message: 'Company name must be 2-100 characters'
  },
  currentPosition: {
    required: false,
    pattern: VALIDATION_PATTERNS.position,
    maxLength: 100,
    message: 'Position must be 2-100 characters'
  },
  experience: {
    required: true,
    validate: (value) => typeof value === 'string' && value.trim().length > 0,
    message: 'Please select a valid experience level'
  },
  skills: {
    required: true,
    pattern: VALIDATION_PATTERNS.skills,
    minLength: 2,
    maxLength: 500,
    message: 'Skills must be 2-500 characters and can only include letters, numbers, commas, and spaces'
  },
  expectedSalary: {
    required: false,
    validate: (value) => {
      if (!value) return true; // Optional field
      const num = parseInt(value);
      return !isNaN(num) && num >= 0 && num <= 10000000; // Max 1 crore
    },
    message: 'Expected salary must be a valid number between 0 and 1,00,00,000'
  },
  noticePeriod: {
    required: false,
    validate: (value) => !value || ['immediate', '15-days', '30-days', '60-days', '90-days'].includes(value),
    message: 'Please select a valid notice period'
  },
  coverLetter: {
    required: false,
    pattern: VALIDATION_PATTERNS.coverLetter,
    maxLength: 2000,
    message: 'Cover letter must not exceed 2000 characters'
  },
  resume: {
    required: true,
    validate: (value) => {
      if (!value) return false;
      return true; // File validation is handled separately in validateFile function
    },
    message: 'Resume is required'
  },

  // New fields validation rules
  status: {
    required: false,
    validate: (value) => !value || ['active', 'inactive', 'hired', 'rejected'].includes(value),
    message: 'Please select a valid status'
  },
  stage: {
    required: false,
    validate: (value) => !value || ['applied', 'screening', 'interview', 'offer', 'hired', 'rejected'].includes(value),
    message: 'Please select a valid stage'
  },
  job: {
    required: false,
    validate: (value) => !value || typeof value === 'string',
    message: 'Invalid job reference'
  },
  jobTitle: {
    required: false,
    pattern: VALIDATION_PATTERNS.position,
    maxLength: 100,
    message: 'Job title must be 2-100 characters'
  },
  department: {
    required: false,
    validate: (value) => !value || ['Engineering', 'Marketing', 'Sales', 'HR', 'Finance', 'Operations', 'Design', 'Product', 'Customer Support', 'Other'].includes(value),
    message: 'Please select a valid department'
  },
  tags: {
    required: false,
    validate: (value) => Array.isArray(value),
    message: 'Tags must be an array'
  },
  linkedin: {
    required: false,
    validate: (value) => {
      if (!value) return true; // Optional field
      // Check if it's a valid URL format and contains linkedin.com
      const urlPattern = /^https?:\/\/.+/i;
      const linkedinPattern = /linkedin\.com/i;
      return urlPattern.test(value) && linkedinPattern.test(value);
    },
    message: 'Please enter a valid LinkedIn URL'
  },
  github: {
    required: false,
    validate: (value) => {
      if (!value) return true; // Optional field
      // Check if it's a valid URL format and contains github.com
      const urlPattern = /^https?:\/\/.+/i;
      const githubPattern = /github\.com/i;
      return urlPattern.test(value) && githubPattern.test(value);
    },
    message: 'Please enter a valid GitHub URL'
  },
  portfolio: {
    required: false,
    validate: (value) => {
      if (!value) return true; // Optional field
      const urlPattern = /^https?:\/\/[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(\/.*)?$/;
      return urlPattern.test(value);
    },
    message: 'Please enter a valid portfolio website URL'
  },
  source: {
    required: false,
    validate: (value) => !value || typeof value === 'string',
    message: 'Please select a valid source'
  },
  notes: {
    required: false,
    maxLength: 3000,
    message: 'Notes must not exceed 3000 characters'
  },
  rating: {
    required: false,
    validate: (value) => {
      if (!value) return true; // Optional field
      const num = parseFloat(value);
      return !isNaN(num) && num >= 0 && num <= 5;
    },
    message: 'Rating must be a number between 0 and 5'
  },
  availability: {
    required: false,
    validate: (value) => !value || ['Available', 'Not Available', 'Available in 2 weeks', 'Available in 1 month', 'Available in 2 months'].includes(value),
    message: 'Please select a valid availability option'
  }
};

// Validate individual field
export const validateField = (name, value) => {
  const rule = VALIDATION_RULES[name];
  if (!rule) return null;

  // Helper function to check if value is empty
  const isEmpty = (val) => {
    if (val === null || val === undefined) return true;
    if (typeof val === 'string') return val.trim() === '';
    if (typeof val === 'number') return isNaN(val);
    if (Array.isArray(val)) return val.length === 0;
    return false;
  };

  // Helper function to get string value for validation
  const getStringValue = (val) => {
    if (typeof val === 'string') return val.trim();
    if (typeof val === 'number') return val.toString();
    if (Array.isArray(val)) return val.join(', ');
    return val ? val.toString() : '';
  };

  // Check required
  if (rule.required && isEmpty(value)) {
    const displayName = getFieldDisplayName(name) || name.charAt(0).toUpperCase() + name.slice(1);
    return `${displayName} is required`;
  }

  // Skip validation if field is empty and not required
  if (!rule.required && isEmpty(value)) {
    return null;
  }

  // Get string value for length and pattern checks
  const stringValue = getStringValue(value);

  // Check min length
  if (rule.minLength && stringValue.length < rule.minLength) {
    return rule.message || `${name} must be at least ${rule.minLength} characters`;
  }

  // Check max length
  if (rule.maxLength && stringValue.length > rule.maxLength) {
    return rule.message || `${name} must not exceed ${rule.maxLength} characters`;
  }

  // Check pattern
  if (rule.pattern && !rule.pattern.test(stringValue)) {
    return rule.message || `Invalid ${name} format`;
  }

  // Check custom validation
  if (rule.validate && !rule.validate(value)) {
    return rule.message || `Invalid ${name}`;
  }

  return null;
};

// Validate entire form
export const validateForm = (formData) => {
  const errors = {};

  Object.keys(VALIDATION_RULES).forEach(field => {
    const error = validateField(field, formData[field]);
    if (error) {
      errors[field] = error;
    }
  });

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

// Format phone number for display
export const formatPhoneNumber = (phone) => {
  if (!phone) return '';

  // Remove all non-digits
  const digits = phone.replace(/\D/g, '');

  // If it starts with 91, remove it
  if (digits.startsWith('91') && digits.length === 12) {
    return digits.substring(2);
  }

  // If it starts with 0, remove it
  if (digits.startsWith('0') && digits.length === 11) {
    return digits.substring(1);
  }

  return digits;
};

// Sanitize input
export const sanitizeInput = (value) => {
  if (typeof value !== 'string') return value;

  return value
    .trim()
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .replace(/[<>]/g, ''); // Remove potential HTML tags
};

// Validate file upload
export const validateFile = (file) => {
  if (!file) return null;

  const allowedTypes = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'image/jpeg',
    'image/png',
    'image/jpg'
  ];

  const maxSize = 25 * 1024 * 1024; // 25MB

  if (!allowedTypes.includes(file.type)) {
    return 'Please upload a PDF, DOC, DOCX, JPG, or PNG file';
  }

  if (file.size > maxSize) {
    return 'File size must be less than 25MB';
  }

  return null;
};

// Get field display name
export const getFieldDisplayName = (fieldName) => {
  const displayNames = {
    firstName: 'First Name',
    lastName: 'Last Name',
    email: 'Email Address',
    phone: 'Phone Number',
    location: 'Location',
    dateOfBirth: 'Date of Birth',
    gender: 'Gender',
    currentCompany: 'Current Company',
    currentPosition: 'Current Position',
    experience: 'Years of Experience',
    education: 'Education',
    skills: 'Skills',
    expectedSalary: 'Expected Salary',
    noticePeriod: 'Notice Period',
    coverLetter: 'Cover Letter'
  };

  return displayNames[fieldName] || fieldName;
};
